import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render errors so a failure shows something readable instead of a
 * blank page.
 *
 * React unmounts the whole tree on an uncaught render error, which gives no
 * clue about what broke unless the console is already open. Anything this
 * catches is a bug, so it prints the real message rather than a friendly
 * euphemism — the audience for this screen is us.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Unhandled render error", error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex h-full items-center justify-center bg-ground p-6">
        <div className="w-full max-w-lg rounded-lg border border-risk bg-surface p-6 shadow-card">
          <h1 className="text-base font-semibold text-risk">Something broke while rendering</h1>
          <p className="mt-2 text-[13px] text-ink-muted">
            This is a bug, not a permissions or connectivity problem. The details below are also in
            the browser console.
          </p>
          <pre className="mt-4 max-h-64 overflow-auto rounded border border-line bg-surface-sunk p-3 text-[12px] text-ink">
            {error.message}
            {error.stack ? `\n\n${error.stack}` : ""}
          </pre>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="mt-4 h-9 rounded-md border border-line px-3 text-sm text-ink hover:bg-surface-sunk"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
